import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './location-modal.style.js';

import { createLocation, getLocationsByCompany } from '@cmms/api';
import { Firestore } from 'firebase/firestore';

@customElement('location-modal')
export class LocationModal extends LitElement {
  @property({ type: Boolean })
  open = false;

  @property({ type: Object })
  formData: {
    name: string;
    description: string;
    address: string;
    location_type: string;
    is_active: boolean;
  } = {
    name: '',
    description: '',
    address: '',
    location_type: '',
    is_active: true
  };

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  static styles = styles;

  connectedCallback() {
    super.connectedCallback();
  }

  private handleInputChange(e: Event, field: string) {
    const target = e.target as HTMLInputElement;
    this.formData = { ...this.formData, [field]: target.value };
  }

  private handleSelectChange(e: CustomEvent) {
    const target = e.target as HTMLSelectElement;
    this.formData = { ...this.formData, [target.name]: target.value };
  }

  private handleCheckboxChange(e: Event, field: string) {
    const target = e.target as HTMLInputElement;
    this.formData = { ...this.formData, [field]: target.checked };
  }

  private normalizeLocationName(name: string): string {
    return name
      .trim() // Remove leading/trailing spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .toLowerCase(); // Convert to lowercase
  }

  private async checkDuplicateLocationName(name: string): Promise<{ isDuplicate: boolean; existingName?: string }> {
    try {
      // Normalize the input name for comparison
      const normalizedInputName = this.normalizeLocationName(name);
      
      // Use the existing API function to get locations by company
      const result = await getLocationsByCompany(this.firestore, this.companyId);
      
      if (result.error) {
        console.error('Error fetching locations for duplicate check:', result.error);
        return { isDuplicate: false }; // Allow submission if we can't check
      }
      
      if (result.data && result.data.length > 0) {
        // Check if any existing location has the same normalized name
        for (const location of result.data) {
          const existingName = location.model?.name;
          if (existingName) {
            const normalizedExistingName = this.normalizeLocationName(existingName);
            if (normalizedExistingName === normalizedInputName) {
              return { isDuplicate: true, existingName: existingName };
            }
          }
        }
      }
      
      return { isDuplicate: false }; // No existing locations, so no duplicates
    } catch (error) {
      console.error('Error checking for duplicate location name:', error);
      // If we can't check for duplicates, allow the submission to proceed
      // The server-side validation will catch any issues
      return { isDuplicate: false };
    }
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    
    // Check if companyId is available
    if (!this.companyId) {
      this.errorMessage = 'Company ID is required';
      return;
    }
    
    if (!this.formData.name || !this.formData.location_type) {
      this.errorMessage = 'Name and location type are required';
      return;
    }

    // Check for duplicate location name
    const duplicateCheck = await this.checkDuplicateLocationName(this.formData.name);
    if (duplicateCheck.isDuplicate) {
      const normalizedExistingName = this.normalizeLocationName(duplicateCheck.existingName!);
      this.errorMessage = `A building with the name '${normalizedExistingName}' already exists`;
      return;
    }

    try {
      this.isLoading = true;
      this.errorMessage = '';

      // Normalize the name before saving
      const normalizedName = this.normalizeLocationName(this.formData.name);

      const locationData = {
        name: normalizedName,
        description: this.formData.description || '',
        address: this.formData.address || '',
        location_type: this.formData.location_type,
        is_active: this.formData.is_active,
        company_id: this.companyId
        // Don't set created_at and updated_at - let the model handle this
      };

      
      const result = await createLocation(this.firestore, locationData);

      if (!result.error) {
        // Dispatch event with the new location
        this.dispatchEvent(new CustomEvent('location-created', {
          detail: result.data,
          bubbles: true,
          composed: true
        }));
        
        // Close the modal after successful creation
        this.handleCancel();
      } else {
        this.errorMessage = result.error || 'Failed to create location';
      }
    } catch (error: any) {
      console.error('Error creating location:', error);
      this.errorMessage = error.message || 'Failed to create location';
    } finally {
      this.isLoading = false;
    }
  }

  private handleCancel() {
    this.closeModal();
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
    this.formData = {
      name: '',
      description: '',
      address: '',
      location_type: '',
      is_active: true
    };
    this.errorMessage = '';
  }

  render() {
    return html`
      <sl-dialog
        label="Add a Location"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        <div class="modal-content">
          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          <form @submit=${this.handleSubmit} 
            @click=${(e: Event) => e.stopPropagation()} 
            @sl-input=${(e: Event) => e.stopPropagation()} 
            @sl-change=${(e: Event) => e.stopPropagation()}
            @sl-open-change=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-hide=${(e: Event) => e.stopPropagation()}
          >
            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="name">Location Name *</label>
              <sl-input
                id="name"
                name="name"
                placeholder="Enter location name"
                value=${this.formData.name}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'name')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                required
                size="large"
              >
                <sl-icon name="map-pin" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="description">Description</label>
              <textarea
                id="description"
                name="description"
                placeholder="Enter location description"
                .value=${this.formData.description}
                @input=${(e: Event) => this.handleInputChange(e, 'description')}
                @click=${(e: Event) => e.stopPropagation()}
                rows="3"
              ></textarea>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="address">Address</label>
              <sl-input
                id="address"
                name="address"
                placeholder="Enter location address"
                value=${this.formData.address}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'address')}
                @sl-change=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-icon name="geo-alt" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" 
              @click=${(e: Event) => e.stopPropagation()}
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
            >
              <label for="location_type">Location Type *</label>
              <sl-select
                id="location_type"
                name="location_type"
                value=${this.formData.location_type}
                @sl-change=${(e: CustomEvent) => {
                  this.handleSelectChange(e);
                  e.stopPropagation();
                }}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                required
              >
                <sl-option value="">Select a location type</sl-option>
                <sl-option value="Office">Office</sl-option>
                <sl-option value="Warehouse">Warehouse</sl-option>
                <sl-option value="Production">Production</sl-option>
                <sl-option value="Maintenance">Maintenance</sl-option>
                <sl-option value="Quality_Control">Quality Control</sl-option>
                <sl-option value="Storage">Storage</sl-option>
                <sl-option value="Shipping">Shipping</sl-option>
                <sl-option value="Receiving">Receiving</sl-option>
                <sl-option value="Other">Other</sl-option>
              </sl-select>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  ?checked=${this.formData.is_active}
                  @change=${(e: Event) => this.handleCheckboxChange(e, 'is_active')}
                  @click=${(e: Event) => e.stopPropagation()}
                >
                Active
              </label>
            </div>

            <div class="form-footer" slot="footer">
              <sl-button
                variant="neutral"
                size="large"
                @click=${this.handleCancel}
                ?disabled=${this.isLoading}
              >
                Cancel
              </sl-button>
              <sl-button
                variant="primary"
                size="large"
                @click=${this.handleSubmit}
                ?loading=${this.isLoading}
                ?disabled=${this.isLoading}
              >
                ${this.isLoading ? 'Creating...' : 'Add'}
              </sl-button>
            </div>
          </form>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'location-modal': LocationModal;
  }
}
